import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router'

import { chatbotsApi } from './api'
import type { ChatbotCreate, ChatbotSettings } from './types'

import { useMyWorkspaces } from '@/features/workspaces/queries'
import { keys } from '@/lib/api/keys'


/** Resolve the workspace id from the slug in the URL. */
export function useWorkspaceId(): string | undefined {
  const { workspaceSlug } = useParams<{ workspaceSlug: string }>()
  const { data } = useMyWorkspaces()
  return data?.find((w) => w.slug === workspaceSlug)?.id
}

export function useChatbots() {
  const ws = useWorkspaceId()
  return useQuery({
    queryKey: keys.chatbots.list(ws ?? ''),
    queryFn: () => chatbotsApi.list(ws!),
    enabled: Boolean(ws),
  })
}

export function useChatbot(id: string | undefined) {
  const ws = useWorkspaceId()
  return useQuery({
    queryKey: keys.chatbots.detail(ws ?? '', id ?? ''),
    queryFn: () => chatbotsApi.get(ws!, id!),
    enabled: Boolean(ws && id),
  })
}

export function useChatbotSettings(id: string | undefined) {
  const ws = useWorkspaceId()
  return useQuery({
    queryKey: keys.chatbots.settings(ws ?? '', id ?? ''),
    queryFn: () => chatbotsApi.getSettings(ws!, id!),
    enabled: Boolean(ws && id),
  })
}

/** What is still missing before the bot can reach `target`. */
export function useReadiness(id: string | undefined, target = 'ready') {
  const ws = useWorkspaceId()
  return useQuery({
    queryKey: keys.chatbots.readiness(ws ?? '', id ?? '', target),
    queryFn: () => chatbotsApi.readiness(ws!, id!, target),
    enabled: Boolean(ws && id),
  })
}

/** The exact prompt the model will receive. */
export function usePromptPreview(id: string | undefined) {
  const ws = useWorkspaceId()
  return useQuery({
    queryKey: keys.chatbots.prompt(ws ?? '', id ?? ''),
    queryFn: () => chatbotsApi.promptPreview(ws!, id!),
    enabled: Boolean(ws && id),
    staleTime: 0,
  })
}

export function useCreateChatbot() {
  const ws = useWorkspaceId()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: ChatbotCreate) => chatbotsApi.create(ws!, body),
    // Not optimistic: the server assigns the id and slug, creates the settings
    // row, the default goal and the website channel.
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.chatbots.list(ws ?? '') })
    },
  })
}

export function useUpdateSettings(id: string) {
  const ws = useWorkspaceId()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (body: Partial<ChatbotSettings>) =>
      chatbotsApi.updateSettings(ws!, id, body),
    onMutate: async (body) => {
      const key = keys.chatbots.settings(ws ?? '', id)
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<ChatbotSettings>(key)
      queryClient.setQueryData<ChatbotSettings>(key, (old) =>
        old ? { ...old, ...body } : old,
      )
      return { previous }
    },
    onError: (_error, _body, context) => {
      queryClient.setQueryData(
        keys.chatbots.settings(ws ?? '', id),
        context?.previous,
      )
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: keys.chatbots.settings(ws ?? '', id),
      })
      // The prompt is composed from these fields, so it is now stale.
      void queryClient.invalidateQueries({ queryKey: keys.chatbots.prompt(ws ?? '', id) })
      void queryClient.invalidateQueries({ queryKey: keys.chatbots.all(ws ?? '') })
    },
  })
}

export function useChangeStatus(id: string) {
  const ws = useWorkspaceId()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (target: string) => chatbotsApi.changeStatus(ws!, id, target),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.chatbots.all(ws ?? '') })
    },
  })
}
